import * as React from 'react'
import { useLocation } from 'react-router-dom'
import MaintenancePage from '@/modules/maintenance/pages/MaintenancePage'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail, normalizeRole } from '@/shared/utils/role'
import { supabase } from '@/lib/supabase'

type ApiResponse = { maintenance: boolean }

export function MaintenanceGate({ children }: { children: React.ReactNode }) {
  // null = ainda não carregado; true/false = valor efetivo vindo do backend
  const [on, setOn] = React.useState<boolean | null>(null)
  const loc = useLocation()
  const { user, profile, loading } = useAuth()
  const path = String(loc.pathname || '/')

  React.useEffect(() => {
    let cancelled = false
    const ctrl = new AbortController()

    ;(async () => {
      try {
        const res = await fetch('/api/maintenance', { cache: 'no-store', signal: ctrl.signal })
        const data = (await res.json()) as ApiResponse
        if (!cancelled) setOn(!!data.maintenance)
      } catch {
        // Fallback direto ao Supabase quando a rota /api/maintenance não existe no dev
        try {
          const { data, error } = await supabase.rpc('get_maintenance_mode')
          if (error) throw error
          const row: any = Array.isArray(data) ? data[0] : data
          if (!cancelled) setOn(!!row?.maintenance)
        } catch {
          // Fail-open: se não conseguimos ler o estado,
          // assumimos modo operacional (sem manutenção).
          if (!cancelled) setOn(false)
        }
      }
    })()

    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [])

  // /login sempre liberado (independente do modo)
  if (path.startsWith('/login')) return <>{children}</>

  // Enquanto o flag não foi carregado (on === null) ou
  // quando explicitamente desligado (false), renderizamos o app normalmente.
  // Isso evita flicker de tela de manutenção e protege SEO
  // quando o site está operacional.
  if (!on) return <>{children}</>

  // Evitar flicker enquanto autenticação está carregando
  if (loading) return <div className="p-6 text-sm text-foreground/70">Carregando...</div>

  // Master logado vê todo o sistema (bypass total)
  const isMaster = !!(
    (user && isMasterByEmail(user.email)) ||
    (normalizeRole(profile?.role) === 'master')
  )
  if (isMaster) { return <>{children}</> }

  // Caso contrário, exibe a tela de manutenção
  return <MaintenancePage />
}

